import { useCallback } from 'react';
import { mergeDeep, Reference } from '@apollo/client/utilities';
import { ClipsRefs } from './common';
import { ClipObject } from '../../../../context/clips';
import { useApolloCacheUtilities } from '../../../../util/apolloUtils';
import { ClipFragment } from '../../../fragments.generated';
import useClipCacheUtilities from './useClipCacheUtilities';
import { ClipsEdge } from './utils/constants';

const useUpdateClipProjectInCache = () => {
  const { client, identityInCache } = useApolloCacheUtilities();
  const { writeClipFragmentRef } = useClipCacheUtilities();
  const updateClipProject = useCallback(
    (
      clip: ClipObject,
      projectToWriteTo: string,
      index: number,
      sequence: string
    ) => {
      // remove the existing clips reference from former project
      const projectToRemoveFrom = clip.projectId;
      client.cache.modify({
        id: identityInCache('Project', projectToRemoveFrom),
        fields: {
          clips_V2: (existingClipsRefs, { readField }) => {
            return {
              ...existingClipsRefs,
              edges: existingClipsRefs.edges.filter(
                (edge: any) => clip.id !== readField('id', edge.node)
              ),
            };
          },
        },
      });
      // overwrite the existing clips reference to new project reference
      // and add this clip reference to the new project
      const clipToBeWritten: ClipFragment = {
        ...clip,
        index,
        sequence,
        projectId: projectToWriteTo,
      };
      const clipRef = writeClipFragmentRef(clipToBeWritten);
      if (!clipRef)
        return console.warn(
          `Failed to write clip fragment during projectId change`
        );
      const newClipEdgeWithNodeRef: Required<
        Omit<ClipsEdge, 'node'> & {
          node: Reference;
        }
      > = {
        __typename: 'ClipsEdge',
        cursor: null,
        node: clipRef,
        highlights: [],
      };
      client.cache.modify({
        id: identityInCache('Project', projectToWriteTo),
        fields: {
          clips_V2: (existingClipsRefs: any): ClipsRefs => {
            const newEdges = [
              ...existingClipsRefs.edges,
              newClipEdgeWithNodeRef,
            ];
            const clipWithNewEdgeResponse = mergeDeep<Array<ClipsRefs>>(
              existingClipsRefs,
              {
                edges: newEdges,
              }
            );
            return clipWithNewEdgeResponse;
          },
        },
      });
    },
    [client, identityInCache, writeClipFragmentRef]
  );
  return updateClipProject;
};
export default useUpdateClipProjectInCache;
